<template>
  <div class="vm-list-vue-results" @click="handlePaginationClick">
    <div v-if="isLoading" class="vm-list-vue-results-overlay">
      <div class="vm-list-vue-results-loader">
        <span class="vm-list-vue-results-spinner" aria-hidden="true"></span>
        <span>Loading VM list...</span>
      </div>
    </div>

    <h4>{{ heading }}</h4>
    <AppPagination
      :pagination="data.pagination"
      aria-label="VM list pagination"
      key-prefix="vm-page"
    />

    <div class="vm-detail-shell" :id="shellId">
      <div class="table-wrapper-scroll-y">
        <table class="table table-striped table-bordered table-condensed vm-detail-table-wrap vm-list-table">
          <thead>
            <tr>
              <th class="vm-list-col vm-list-col--name"><div class="text-center">Name</div></th>
              <th class="vm-list-col vm-list-col--status"><div class="text-center">Status</div></th>
              <th class="vm-list-col vm-list-col--cpu hidden-tablet"><div class="text-center">CPU</div></th>
              <th class="vm-list-col vm-list-col--memory hidden-tablet"><div class="text-center">Memory</div></th>
              <th class="vm-list-col vm-list-col--boot hidden-note"><div class="text-center">Boot</div></th>
              <th class="vm-list-col vm-list-col--network hidden-phone"><div class="text-center">Network</div></th>
              <th class="vm-list-col vm-list-col--owner"><div class="text-center">Owner</div></th>
              <th class="vm-list-col vm-list-col--operation"><div class="text-center">Operation</div></th>
              <th class="vm-list-col vm-list-col--actions"><div class="text-center">Actions</div></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in data.rows || []" :key="`vm-${row.id}`" :id="`vm-row-${row.id}`">
              <td>
                <div class="text-left vm-list-name-cell">
                  <template v-if="row.name?.can_open && row.name?.detail_url">
                    <a
                      :href="`/vms/show/${row.id}`"
                      class="vm-detail-link"
                      @click.prevent.stop="handleOpenVmDetail(row.name.detail_url)"
                    >
                      <AppOsIcon :symbol="row.name.os_icon_symbol" />
                      {{ row.name.text }}
                    </a>
                  </template>
                  <template v-else>
                    <AppOsIcon :symbol="row.name?.os_icon_symbol" />
                    {{ row.name?.text }}
                  </template>

                  <template v-if="row.name?.screenshot_url">
                    <a :href="row.name.screenshot_url" target="_blank" rel="noopener noreferrer">
                      <AppIcon name="camera" />
                    </a>
                  </template>
                </div>
                <div v-if="row.comment" class="vm-list-name-comment">
                  {{ row.comment }}
                </div>
              </td>
              <td class="text-center">
                <span class="app-status-chip" :class="row.state_class">{{ row.state_label }}</span>
                <a
                  v-if="row.storage_migration?.active"
                  :href="row.storage_migration.href"
                  class="vm-list-storage-migration"
                  :title="row.storage_migration.title"
                >
                  <span class="vm-list-storage-migration__spinner" aria-hidden="true"></span>
                  <span>{{ row.storage_migration.label }}</span>
                  <span class="vm-list-storage-migration__percent">{{ row.storage_migration.percent }}%</span>
                </a>
              </td>
              <td class="hidden-tablet"><div class="text-center">{{ row.vcpu }}</div></td>
              <td class="hidden-tablet"><div class="text-center">{{ row.memory }}</div></td>
              <td class="hidden-note"><div class="text-center">{{ row.boot }}</div></td>
              <td class="hidden-phone"><div class="text-center">{{ row.network }}</div></td>
              <td>
                <div class="text-center">
                  <template v-if="row.owner?.url">
                    <a :href="row.owner.url">{{ row.owner.text }}</a>
                  </template>
                  <template v-else>
                    {{ row.owner?.text }}
                  </template>
                </div>
              </td>
              <td class="nopadding">
                <div class="text-center">
                  <div class="app-action-buttons vm-list-action-buttons">
                    <template v-for="action in orderedPrimaryActions(row)" :key="action.key">
                      <button
                        v-if="action.kind === 'link' && action.visible && action.enabled && action.consoleOptions?.length"
                        type="button"
                        class="btn btn-sm btn-outline-primary app-action-btn app-action-btn--primary app-action-btn--icon vm-list-console-actions__trigger"
                        :class="action.hiddenClass"
                        :title="action.title"
                        @click="toggleConsoleMenu(row.id, $event)"
                      >
                        <AppIcon :name="action.icon" />
                        <AppIcon name="chevron-down" class="vm-list-console-actions__caret" />
                      </button>

                      <button
                        v-else-if="action.kind === 'form' && action.visible && action.enabled"
                        type="button"
                        class="btn btn-sm btn-outline-primary app-action-btn app-action-btn--primary app-action-btn--icon"
                        :class="action.hiddenClass"
                        :title="action.title"
                        @click="handlePrimaryAction(action)"
                      >
                        <AppIcon :name="action.icon" />
                      </button>

                      <button
                        v-else-if="action.key === 'migration' && action.visible && action.enabled"
                        type="button"
                        class="btn btn-sm btn-outline-primary app-action-btn app-action-btn--primary app-action-btn--icon"
                        :class="action.hiddenClass"
                        :title="action.title"
                        @click="handleMigrationAction(action)"
                      >
                        <AppIcon :name="action.icon" />
                      </button>

                      <a
                        v-else-if="action.kind === 'link' && action.visible && action.enabled && !action.openInModal"
                        :href="action.href"
                        class="btn btn-sm btn-outline-primary app-action-btn app-action-btn--primary app-action-btn--icon"
                        :class="action.hiddenClass"
                        :title="action.title"
                        :target="action.target || null"
                        v-bind="action.dataAttrs"
                      >
                        <AppIcon :name="action.icon" />
                      </a>

                      <button
                        v-else-if="action.kind === 'link' && action.visible && action.enabled && action.openInModal"
                        type="button"
                        class="btn btn-sm btn-outline-primary app-action-btn app-action-btn--primary app-action-btn--icon"
                        :class="action.hiddenClass"
                        :title="action.title"
                        @click="handleConsoleAction(action, $event, row.id)"
                      >
                        <AppIcon :name="action.icon" />
                      </button>

                      <button
                        v-else-if="action.visible"
                        type="button"
                        class="btn btn-sm disabled btn-outline-secondary app-action-btn app-action-btn--ghost app-action-btn--icon"
                        :class="action.hiddenClass"
                        disabled
                      >
                        <AppIcon :name="action.icon" />
                      </button>
                    </template>
                  </div>

                  <div
                    v-if="openConsoleMenuId === row.id && consoleMenuOptions(row).length"
                    class="vm-list-more-actions__menu vm-list-console-actions__menu"
                    :style="consoleMenuStyle"
                    @click.stop
                  >
                    <template v-for="item in consoleMenuOptions(row)" :key="`console-${row.id}-${item.key}`">
                      <a
                        v-if="item.target"
                        :href="item.href"
                        class="vm-list-more-actions__item"
                        :target="item.target"
                        rel="noopener noreferrer"
                        @click="closeConsoleMenu"
                      >
                        <AppIcon :name="item.icon" />
                        <span>{{ item.label }}</span>
                      </a>

                      <button
                        v-else
                        type="button"
                        class="vm-list-more-actions__item"
                        @click.stop="handleConsoleMenuAction(item, $event, row.id)"
                      >
                        <AppIcon :name="item.icon" />
                        <span>{{ item.label }}</span>
                      </button>
                    </template>
                  </div>
                </div>
              </td>
              <td>
                <div class="text-center vm-list-more-actions" v-if="hasSecondaryActions(row)">
                  <button
                    type="button"
                    class="vm-list-more-actions__gear"
                    :aria-expanded="openMenuId === row.id ? 'true' : 'false'"
                    @click.stop="toggleMenu(row.id, $event)"
                  >
                    <AppIcon name="settings" />
                    <AppIcon name="chevron-down" class="vm-list-more-actions__caret" />
                  </button>

                  <div
                    v-if="openMenuId === row.id"
                    class="vm-list-more-actions__menu"
                    :style="menuStyle"
                    @click.stop
                  >
                    <template v-for="action in orderedSecondaryActions(row)" :key="`more-${row.id}-${action.key}`">
                      <button
                        v-if="action.kind === 'link' && action.visible && action.enabled && action.dataAttrs && action.dataAttrs['data-snapshot-create-url']"
                        type="button"
                        class="vm-list-more-actions__item"
                        @click.stop="openVmSnapshotCreateModal(action)"
                      >
                        <AppIcon :name="action.icon" />
                        <span>{{ action.label }}</span>
                      </button>


                      <button
                        v-else-if="action.key === 'consolidate_disk' && action.visible && action.enabled"
                        type="button"
                        class="vm-list-more-actions__item"
                        @click.stop="handleConsolidateDiskAction(action)"
                      >
                        <AppIcon :name="action.icon" />
                        <span>{{ action.label }}</span>
                      </button>

                      <button
                        v-else-if="action.key === 'templateize' && action.visible && action.enabled"
                        type="button"
                        class="vm-list-more-actions__item"
                        @click.stop="handleTemplateizeAction(action)"
                      >
                        <AppIcon :name="action.icon" />
                        <span>{{ action.label }}</span>
                      </button>

                      <a
                        v-else-if="action.kind === 'link' && action.visible && action.enabled"
                        :href="action.href"
                        class="vm-list-more-actions__item"
                        :target="action.target || null"
                        v-bind="action.dataAttrs"
                      >
                        <AppIcon :name="action.icon" />
                        <span>{{ action.label }}</span>
                      </a>

                      <span
                        v-else-if="action.kind === 'link' && action.visible"
                        class="vm-list-more-actions__item vm-list-more-actions__item--disabled"
                      >
                        <AppIcon :name="action.icon" />
                        <span>{{ action.label }}</span>
                      </span>

                      <button
                        v-else-if="action.kind === 'form' && action.visible && action.enabled"
                        type="button"
                        class="vm-list-more-actions__item vm-list-more-actions__item--danger"
                        @click.stop="handleSecondaryDelete(action, row.id)"
                      >
                        <AppIcon :name="action.icon" />
                        <span>{{ action.label }}</span>
                      </button>

                      <span
                        v-else-if="action.kind === 'form' && action.visible"
                        class="vm-list-more-actions__item vm-list-more-actions__item--disabled"
                      >
                        <AppIcon :name="action.icon" />
                        <span>{{ action.label }}</span>
                      </span>
                    </template>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <aside
        :id="`${shellId}-panel`"
        class="vm-detail-panel"
        :style="vmDetailPanelStyle"
        :aria-hidden="vmDetail.open && vmDetail.mode === 'panel' ? 'false' : 'true'"
      >
        <VmDetailPanel
          :open="vmDetail.open && vmDetail.mode === 'panel'"
          :title="vmDetail.title"
          :detail-data="vmDetail.detailData"
          :ip-refresh-url="vmDetail.ipRefreshUrl"
          @close="closeVmDetail"
        />
      </aside>
      <VmDetailModal
        :modal-id="vmDetailModalId"
        :open="vmDetail.open && vmDetail.mode === 'modal'"
        :title="vmDetail.title"
        :detail-data="vmDetail.detailData"
        :ip-refresh-url="vmDetail.ipRefreshUrl"
        @close="closeVmDetail"
      />
    </div>
    <SerialConsoleWindow
      v-for="windowItem in visibleConsoleWindows"
      :key="windowItem.id"
      :open="true"
      :src="windowItem.src"
      :title="windowItem.title"
      :status-badge-text="windowItem.statusBadgeText || ''"
      :status-badge-tone="windowItem.statusBadgeTone || 'normal'"
      :z-index="windowItem.zIndex"
      :initial-offset="windowItem.offset"
      :enter-from="windowItem.enterFrom || null"
      :minimize-to="minimizeTargetPoint(windowItem.id)"
      @focus="focusSerialConsoleWindow(windowItem.id)"
      @minimize="minimizeSerialConsoleWindow(windowItem.id)"
      @close="closeSerialConsoleWindow(windowItem.id)"
    />

    <div v-if="snapshotModal.open" class="vm-snapshot-modal-backdrop" @click="closeVmSnapshotCreateModal">
      <div class="vm-snapshot-modal" role="dialog" aria-modal="true" aria-label="Create VM Snapshot" @click.stop>
        <div v-if="snapshotModal.creating" class="vm-snapshot-modal__progress">
          <span class="vm-list-vue-results-spinner" aria-hidden="true"></span>
          <span>{{ snapshotModal.progressText || "Creating snapshot..." }}</span>
        </div>
        <h4>Create Snapshot</h4>
        <p class="text-muted" style="margin-bottom:8px;">{{ snapshotModal.vmName || 'VM' }}</p>
        <div class="form-group">
          <label>Snapshot name</label>
          <input v-model="snapshotModal.name" :disabled="snapshotModal.creating" type="text" class="form-control" placeholder="snap-20260518-1205" />
        </div>
        <div class="form-group" style="margin-top:8px;">
          <label>Description (optional)</label>
          <input v-model="snapshotModal.description" :disabled="snapshotModal.creating" type="text" class="form-control" placeholder="before update" />
        </div>
        <div style="margin-top:12px; display:flex; gap:8px; justify-content:flex-end;">
          <button type="button" class="btn btn-default" :disabled="snapshotModal.creating" @click="closeVmSnapshotCreateModal">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="snapshotModal.creating" @click="submitVmSnapshotCreate">{{ snapshotModal.creating ? "Creating..." : "Create" }}</button>
        </div>
      </div>
    </div>

    <div v-if="minimizedConsoleWindows.length" class="vm-console-dock" aria-label="Minimized consoles">
      <TransitionGroup name="console-dock-transition" tag="div" class="vm-console-dock__list">
        <div
          v-for="windowItem in minimizedConsoleWindows"
          :key="`dock-${windowItem.id}`"
          class="vm-console-dock__item"
        >
          <button
            type="button"
            class="vm-console-dock__restore"
            :title="`Restore ${windowItem.title}`"
            @click="restoreSerialConsoleWindow(windowItem.id, $event)"
          >
            <AppIcon name="terminal" />
            <span>{{ windowItem.title }}</span>
          </button>
          <button
            type="button"
            class="vm-console-dock__close"
            :title="`Close ${windowItem.title}`"
            @click="closeSerialConsoleWindow(windowItem.id)"
          >
            <AppIcon name="close" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppIcon from './AppIcon.vue'
import AppPagination from './AppPagination.vue'
import AppOsIcon from './AppOsIcon.vue'
import SerialConsoleWindow from './SerialConsoleWindow.vue'
import VmDetailModal from './VmDetailModal.vue'
import VmDetailPanel from './VmDetailPanel.vue'
import { sccGetModalInstance, sccShowModal } from '../composables/bridges/app_core'

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({ rows: [], pagination: null, shell_id: 'vm-detail-shell', heading: 'Virtual Machines' })
  },
  initialLoading: {
    type: Boolean,
    default: false
  }
})

const data = ref(props.initialData || { rows: [], pagination: null, shell_id: 'vm-detail-shell', heading: 'Virtual Machines' })
const isLoading = ref(!!props.initialLoading)
const shellId = computed(() => data.value.shell_id || 'vm-detail-shell')
const heading = computed(() => data.value.heading || 'Virtual Machines')
const serialConsoleWindows = ref([])
const serialConsoleWindowSeed = ref(0)
const serialConsoleZSeed = ref(1080)
const snapshotModal = ref({ open: false, createUrl: '', listUrl: '', vmName: '', name: '', description: '', creating: false, progressText: '' })
const openMenuId = ref(null)
const menuStyle = ref({})
const openConsoleMenuId = ref(null)
const consoleMenuStyle = ref({})
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
const vmDetail = ref({
  open: false,
  mode: 'panel',
  title: '',
  detailData: null,
  ipRefreshUrl: ''
})
const vmDetailPanelStyle = ref({})
const vmDetailModalId = computed(() => `show_vm_detail_${shellId.value}`)
const visibleConsoleWindows = computed(() => serialConsoleWindows.value.filter((item) => !item.minimized))
const minimizedConsoleWindows = computed(() => serialConsoleWindows.value.filter((item) => item.minimized))
let storageMigrationTimer = null
let storageMigrationPollInFlight = false

function hideCloneOverlay() {
  window.sccStatusShell?.hideCloneOverlay?.()
}

function markCloneProgressFailed(message) {
  window.sccStatusShell?.markCloneProgressFailed?.(message)
}

function sccBeginClonePolling(statusUrl) {
  window.sccStatusShell?.sccBeginClonePolling?.(statusUrl)
}

function sccDismissProgressToast() {
  window.sccStatusShell?.sccDismissProgressToast?.()
}

function sccFormatCloneBytes(bytes) {
  return window.sccStatusShell?.sccFormatCloneBytes?.(bytes) || ""
}

function sccSetCloneProgress(percent) {
  window.sccStatusShell?.sccSetCloneProgress?.(percent)
}

function sccShowCloneOverlay(vmName) {
  window.sccStatusShell?.sccShowCloneOverlay?.(vmName)
}

function sccStartStatusPollingFromResponse(data, options = {}) {
  return window.sccStatusShell?.sccStartStatusPollingFromResponse?.(data, options)
}

function sccShowToast(level, message) {
  window.sccStatusShell?.sccShowToast?.(level, message)
}

function startSccProgress(message) {
  return window.sccStatusShell?.startSccProgress?.(message)
}

function setSccOverlayTexts(options = {}) {
  window.sccStatusShell?.setSccOverlayTexts?.(options)
}

function startInlineUploadOverlay(title, text) {
  window.sccStatusShell?.startInlineUploadOverlay?.(title, text)
}

function ensureModalContainer(modalId) {
  let modal = document.getElementById(modalId)

  if (!modal) {
    modal = document.createElement('div')
    modal.id = modalId
    modal.className = 'modal fade'
    modal.setAttribute('role', 'dialog')
    modal.setAttribute('aria-hidden', 'true')
    document.body.appendChild(modal)
  } else if (modal.parentNode !== document.body) {
    document.body.appendChild(modal)
  }

  return modal
}

function resetModalArtifacts(modalOrJquery) {
  const modal = modalOrJquery && modalOrJquery.jquery ? modalOrJquery.get(0) : modalOrJquery
  let instance

  if (modal && modal.getAttribute && modal.getAttribute('data-vue-component')) {
    modal.dispatchEvent(new CustomEvent('scc:modal:close'))
  }

  if (modal) {
    instance = sccGetModalInstance(modal, false)
    if (instance && typeof instance.dispose === 'function') {
      instance.dispose()
    }

    modal.classList.remove('in', 'hide', 'show')
    modal.style.display = 'none'
    modal.setAttribute('aria-hidden', 'true')
    modal.style.paddingRight = ''
  }

  document.querySelectorAll('.modal-backdrop').forEach((backdrop) => {
    backdrop.remove()
  })
  document.body.classList.remove('modal-open')
  document.body.style.paddingRight = ''
}

function handleUpdate(event) {
  data.value = event.detail || { rows: [], pagination: null, shell_id: 'vm-detail-shell', heading: 'Virtual Machines' }
  openMenuId.value = null
  openConsoleMenuId.value = null
  menuStyle.value = {}
  consoleMenuStyle.value = {}
}

function shouldUseVmDetailPanel() {
  const shell = document.getElementById(shellId.value)
  const panel = document.getElementById(`${shellId.value}-panel`)
  if (!shell || !panel || window.innerWidth <= 991) {
    return false
  }

  const shellRect = shell.getBoundingClientRect()
  const availableRight = Math.max(0, window.innerWidth - shellRect.right - 18)
  return availableRight >= 280
}

function layoutVmDetailPanel() {
  const shell = document.getElementById(shellId.value)
  const table = shell ? shell.querySelector('.vm-detail-table-wrap') : null
  const footer = document.querySelector('.footer')
  const gap = 18
  if (!shell) {
    return
  }

  const shellRect = shell.getBoundingClientRect()
  const shellWidth = shell.offsetWidth
  const footerHeight = footer ? footer.offsetHeight : 0
  const viewportAvailableHeight = Math.max(320, window.innerHeight - Math.max(shellRect.top, 0) - footerHeight - gap)
  const panelHeight = Math.max(table ? table.offsetHeight : shell.offsetHeight, viewportAvailableHeight)

  if (window.innerWidth <= 991) {
    vmDetailPanelStyle.value = {
      left: '0px',
      width: `${shellWidth}px`,
      minHeight: `${panelHeight}px`,
      height: `${panelHeight}px`
    }
    return
  }

  const minPreferredWidth = 400
  const maxPreferredWidth = 560
  const availableRight = Math.max(0, window.innerWidth - shellRect.right - gap)
  const preferredWidth = Math.min(maxPreferredWidth, Math.max(minPreferredWidth, availableRight))
  let left = shellWidth + gap

  if (availableRight < preferredWidth) {
    left = Math.max(24, left - (preferredWidth - availableRight))
  }

  vmDetailPanelStyle.value = {
    left: `${left}px`,
    width: `${preferredWidth}px`,
    minHeight: `${panelHeight}px`,
    height: `${panelHeight}px`
  }
}

function closeVmDetail() {
  const shell = document.getElementById(shellId.value)
  if (shell) {
    shell.classList.remove('vm-detail-shell-open')
  }

  vmDetail.value = {
    open: false,
    mode: vmDetail.value.mode,
    title: '',
    detailData: null,
    ipRefreshUrl: ''
  }
}

function rerenderVmDetailForViewport() {
  if (!vmDetail.value.open) {
    return
  }

  const usePanel = shouldUseVmDetailPanel()
  const nextMode = usePanel ? 'panel' : 'modal'
  if (nextMode !== vmDetail.value.mode) {
    vmDetail.value = {
      ...vmDetail.value,
      mode: nextMode
    }
  }

  const shell = document.getElementById(shellId.value)
  if (shell) {
    if (nextMode === 'panel') {
      shell.classList.add('vm-detail-shell-open')
      layoutVmDetailPanel()
    } else {
      shell.classList.remove('vm-detail-shell-open')
    }
  }
}

function handleLoading(event) {
  const payload = event.detail || {}
  if (payload.eventName !== 'scc:vm-list-results:update') {
    return
  }

  isLoading.value = !!payload.loading
}

function handleRowUpdate(event) {
  const payload = event.detail || {}
  const rowData = payload.rowData

  if (!rowData || !rowData.id) {
    return
  }

  const rows = [...(data.value.rows || [])]
  const index = rows.findIndex((row) => String(row.id) === String(rowData.id))

  if (index === -1) {
    return
  }

  rows.splice(index, 1, rowData)
  data.value = {
    ...data.value,
    rows
  }
}

function handleVmStateChanged(event) {
  const payload = event.detail || {}
  const vmId = payload.vm_id

  if (!vmId) {
    return
  }

  const rows = [...(data.value.rows || [])]
  const index = rows.findIndex((row) => String(row.id) === String(vmId))

  if (index === -1) {
    return
  }

  if (payload.type === 'removed') {
    rows.splice(index, 1)
    data.value = { ...data.value, rows }
    closeConsoleWindowsByVmId(vmId)
    if (String(openMenuId.value) === String(vmId)) {
      openMenuId.value = null
      menuStyle.value = {}
    }
    if (String(openConsoleMenuId.value) === String(vmId)) {
      openConsoleMenuId.value = null
      consoleMenuStyle.value = {}
    }
    return
  }

  const row = { ...rows[index] }
  const nextStateValue = Number(payload.state_value)
  row.state_label = payload.state_label || row.state_label
  row.state_class = payload.state_class || row.state_class
  row.operation = recalculateOperationState(row.operation || {}, row.id, nextStateValue)
  row.migration = recalculateMigrationState(row.migration || {}, row.id, nextStateValue)

  rows.splice(index, 1, row)
  data.value = { ...data.value, rows }

  if (nextStateValue !== 2) {
    closeConsoleWindowsByVmId(vmId)
  }
}

async function pollStorageMigrations() {
  if (storageMigrationPollInFlight) return

  const activeRows = (data.value.rows || []).filter((row) => row.storage_migration?.active && row.storage_migration?.status_url)
  if (!activeRows.length) return

  storageMigrationPollInFlight = true
  try {
    const results = await Promise.allSettled(activeRows.map(async (row) => ({
      vmId: row.id,
      progress: await requestJson(row.storage_migration.status_url, { method: 'GET', cache: 'no-store' })
    })))
    const updates = new Map(results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => [String(result.value.vmId), result.value.progress]))
    if (!updates.size) return

    const rows = (data.value.rows || []).map((existingRow) => {
      const progress = updates.get(String(existingRow.id))
      if (!progress || !existingRow.storage_migration?.active) return existingRow

      const row = { ...existingRow }
      if (progress.status === 'completed' || progress.status === 'failed') {
        const migrateAction = row.operation?.migrate_storage
        row.storage_migration = null
        if (migrateAction) {
          row.operation = {
            ...row.operation,
            migrate_storage: {
              ...migrateAction,
              progress_active: false,
              href: existingRow.storage_migration.migrate_url,
              title: 'Migrate VM Disk to Another Storage',
              label: 'Migrate Storage'
            }
          }
        }
      } else {
        row.storage_migration = {
          ...row.storage_migration,
          percent: Math.max(0, Math.min(100, Number(progress.percent) || 0))
        }
      }
      return row
    })
    data.value = { ...data.value, rows }
  } finally {
    storageMigrationPollInFlight = false
  }
}

function recalculateOperationState(operation, vmId, stateValue) {
  const next = JSON.parse(JSON.stringify(operation || {}))
  const vmName = extractVmName(operation)
  const isUp = stateValue === 2
  const isPause = stateValue === 3
  const isUnknown = stateValue === 4
  const isTemplate = stateValue === 5
  const canStart = stateValue === 1 || isPause || isUnknown

  if (next.start) {
    next.start.enabled = canStart
    next.start.form = isPause ? `/vms/resume/${vmId}.json` : `/vms/start/${vmId}.json`
    next.start.progress_message = `${isPause ? 'Resuming' : 'Starting'} ${vmName} ... Please wait`
    next.start.title = isPause ? 'Resume VM' : 'Start VM'
  }

  if (next.stop) {
    next.stop.enabled = isUp
  }

  if (next.pause) {
    next.pause.enabled = isUp
  }

  if (next.reboot) {
    next.reboot.enabled = !isTemplate
  }

  if (next.snapshot) {
    next.snapshot.enabled = !!next.snapshot.visible && !isUp && !isTemplate
  }

  if (next.consolidate_disk) {
    next.consolidate_disk.enabled = !!next.consolidate_disk.visible && stateValue === 1
  }

  if (next.migrate_storage) {
    next.migrate_storage.enabled = !!next.migrate_storage.visible &&
      (!!next.migrate_storage.progress_active || stateValue === 1 ||
        (stateValue === 2 && !!next.migrate_storage.online_enabled))
  }

  if (next.download_xml) {
    next.download_xml.enabled = !isTemplate
  }

  if (next.vnc) {
    next.vnc.enabled = !!next.vnc.visible && isUp
  }

  return next
}

function recalculateMigrationState(migration, vmId, stateValue) {
  const next = { ...(migration || {}) }
  const disabled = stateValue === 5 || stateValue === 3 || stateValue === 4
  next.enabled = !!next.visible && !disabled
  next.url = next.enabled ? `/vms/migrate/${vmId}.json` : null
  return next
}

function extractVmName(operation) {
  const message = operation?.start?.progress_message || ''
  return message.replace(/^(Resuming|Starting)\s+/, '').replace(/\s+\.\.\.\s+Please wait$/, '') || 'VM'
}

function orderedActions(operation = {}) {
  return [
    normalizeFormAction('start', operation.start),
    normalizeFormAction('stop', operation.stop),
    normalizeFormAction('pause', operation.pause),
    normalizeFormAction('reboot', operation.reboot),
    normalizeLinkAction('snapshot', operation.snapshot),
    normalizeLinkAction('download_xml', operation.download_xml),
    normalizeLinkAction('vnc', operation.vnc),
    normalizeLinkAction('edit', operation.edit)
  ].filter(Boolean)
}

function orderedPrimaryActions(row) {
  const primaryActions = orderedActions(row.operation).filter((action) =>
    ['start', 'stop', 'pause', 'reboot', 'vnc'].includes(action.key)
  )

  const migrationAction = normalizeMigrationAction(row.migration)
  if (migrationAction) {
    primaryActions.push(migrationAction)
  }

  return primaryActions
}

function orderedSecondaryActions(row) {
  return [
    normalizeLinkAction('edit', row.operation?.edit, 'Edit'),
    normalizeLinkAction('templateize', row.operation?.templateize, 'Create Template'),
    normalizeLinkAction('snapshot', row.operation?.snapshot, 'Create Snapshot'),
    normalizeLinkAction('snapshot_list', row.operation?.snapshot_list, 'Snapshot List'),
    normalizeFormAction('consolidate_disk', row.operation?.consolidate_disk, 'Consolidate Disk'),
    normalizeLinkAction('migrate_storage', row.operation?.migrate_storage),
    normalizeLinkAction('download_xml', row.operation?.download_xml, 'Download XML'),
    normalizeFormAction('delete', row.operation?.delete, 'Delete')
  ].filter(Boolean)
}

function consoleMenuOptions(row) {
  const action = orderedPrimaryActions(row).find((item) => item.key === 'vnc')
  return action?.consoleOptions || []
}

function hasSecondaryActions(row) {
  return orderedSecondaryActions(row).some((action) => action.visible)
}

async function handlePrimaryAction(action) {
  if (!action?.form) {
    return
  }

  if (action.confirmMessage && !window.confirm(action.confirmMessage)) {
    return
  }

  if (action.progressMessage) {
    startSccProgress(action.progressMessage)
  }

  try {
    const response = await requestJson(action.form, {
      method: 'POST'
    })

    sccDismissProgressToast()

    if (response && response.level && response.message) {
      sccShowToast(response.level, response.message)
    }

    if (response && response.row_data) {
      document.dispatchEvent(new CustomEvent('scc:vm-list-row:update', {
        detail: {
          vmId: response.vm_id,
          rowData: response.row_data
        }
      }))
      return
    }

    if (response && response.vm_id && response.row_html) {
      replaceVmRow(response.vm_id, response.row_html)
    }
  } catch (error) {
    sccDismissProgressToast()
    sccShowToast('danger', error.message || 'VM action failed.')
  }
}

async function handleConsolidateDiskAction(action) {
  if (!action?.form) return
  if (action.confirmMessage && !window.confirm(action.confirmMessage)) return

  closeMenu()
  sccShowCloneOverlay(action.vmName || "VM disk")
  try {
    const response = await requestJson(action.form, { method: "POST" })
    if (!response?.status_url) {
      throw new Error("Disk consolidation progress URL was not returned.")
    }
    sccBeginClonePolling(response.status_url)
  } catch (error) {
    markCloneProgressFailed(error.message || "Failed to start disk consolidation.")
  }
}

async function handleSecondaryDelete(action, vmId) {
  if (!action?.form) {
    return
  }

  if (action.confirmMessage && !window.confirm(action.confirmMessage)) {
    return
  }

  try {
    const response = await requestJson(action.form, {
      method: 'DELETE'
    })

    if (response && response.level && response.message) {
      sccShowToast(response.level, response.message)
    }

    const nextRows = [...(data.value.rows || [])].filter((row) => String(row.id) !== String(vmId))
    data.value = {
      ...data.value,
      rows: nextRows
    }

    closeMenu()
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to delete VM.')
  }
}


function defaultSnapshotName() {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  const hh = String(now.getHours()).padStart(2, '0')
  const mm = String(now.getMinutes()).padStart(2, '0')
  return `snap-${y}${m}${d}-${hh}${mm}`
}

function openVmSnapshotCreateModal(action) {
  const createUrl = action?.dataAttrs?.['data-snapshot-create-url'] || ''
  if (!createUrl) {
    sccShowToast('danger', 'Snapshot API URL is not available.')
    return
  }
  snapshotModal.value = {
    open: true,
    createUrl: action?.dataAttrs?.['data-snapshot-create-url'] || '',
    listUrl: action?.dataAttrs?.['data-snapshot-list-url'] || action?.href || '',
    vmName: action?.dataAttrs?.['data-snapshot-vm-name'] || 'VM',
    name: defaultSnapshotName(),
    description: '',
    creating: false,
    progressText: ''
  }
  closeMenu()
}

function closeVmSnapshotCreateModal() {
  if (snapshotModal.value.creating) return
  snapshotModal.value.open = false
}

async function submitVmSnapshotCreate() {
  const createUrl = snapshotModal.value.createUrl
  const listUrl = snapshotModal.value.listUrl
  const snapshotName = String(snapshotModal.value.name || '').trim() || defaultSnapshotName()

  if (!createUrl) {
    sccShowToast('danger', 'Snapshot create URL is not available.')
    return
  }
  try {
    snapshotModal.value.creating = true
    snapshotModal.value.progressText = `Creating snapshot for ${snapshotModal.value.vmName || 'VM'}...`

    const body = new URLSearchParams()
    body.set('snapshot_name', snapshotName)
    if (snapshotModal.value.description) {
      body.set('description', snapshotModal.value.description)
    }

    const response = await requestJson(createUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: body.toString()
    })

    closeVmSnapshotCreateModal()
    sccShowToast(response?.level || 'success', response?.message || 'Snapshot created.')
    if (listUrl) {
      window.location.href = listUrl
    }
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to create snapshot.')
  } finally {
    snapshotModal.value.creating = false
    snapshotModal.value.progressText = ''
  }
}

async function handleTemplateizeAction(action) {
  const templateizeUrl = action?.dataAttrs?.['data-templateize-url'] || action?.href
  const templateizeName = action?.dataAttrs?.['data-templateize-name'] || 'VM'
  const confirmMessage = action?.dataAttrs?.['data-templateize-confirm']

  if (!templateizeUrl) {
    return
  }

  if (confirmMessage && !window.confirm(confirmMessage)) {
    return
  }

  closeMenu()
  startInlineUploadOverlay(`Creating template from ${templateizeName}`, 'Preparing template creation...')

  try {
    const response = await requestJson(templateizeUrl, {
      method: 'POST'
    })

    beginTemplateizePolling(response?.status_url)
  } catch (error) {
    markCloneProgressFailed(error.message || 'Failed to start template creation.')
  }
}

function beginTemplateizePolling(statusUrl) {
  if (!statusUrl) {
    markCloneProgressFailed('Template creation progress URL was not returned.')
    return
  }

  let timer = null

  const finish = () => {
    if (timer) {
      window.clearInterval(timer)
      timer = null
    }
  }

  const poll = async () => {
    try {
      const payload = await requestJson(statusUrl, {
        method: 'GET',
        cache: 'no-store'
      })

      {
        const bytes = payload.copied_bytes && payload.total_bytes && sccFormatCloneBytes
          ? `${sccFormatCloneBytes(payload.copied_bytes)} / ${sccFormatCloneBytes(payload.total_bytes)}`
          : ''

        setSccOverlayTexts({
          title: `Creating template from ${payload.vm_name || 'VM'}`,
          text: payload.message || 'Creating template...',
          bytes
        })
      }

      sccSetCloneProgress(payload.percent || 0)

      if (payload.status === 'completed') {
        finish()
        setSccOverlayTexts({
          text: payload.message || 'Template creation completed.',
          bytes: ''
        })
        sccShowToast('success', payload.message || 'Template creation completed.')
        if (payload.warning_message) {
          sccShowToast('warning', payload.warning_message)
        }
        window.setTimeout(() => {
          hideCloneOverlay()
        }, 900)
      } else if (payload.status === 'failed') {
        finish()
        markCloneProgressFailed(payload.error || payload.message || 'Template creation failed.')
      }
    } catch (_error) {
      finish()
      markCloneProgressFailed('Failed to read template creation progress.')
    }
  }

  poll()
  timer = window.setInterval(poll, 1000)
}

async function handleMigrationAction(action) {
  if (!action?.href) {
    return
  }

  try {
    const response = await requestJson(action.href, {
      method: 'GET',
      cache: 'no-store'
    })

    const modal = ensureModalContainer(`migrate_${response.vm_id}`)
    resetModalArtifacts(modal)
    if (
      modal.getAttribute('data-vue-component') === 'vm-migrate-modal' &&
      modal.getAttribute('data-vue-mounted') === 'true'
    ) {
      modal.dispatchEvent(new CustomEvent('scc:vm-migrate-modal:update', {
        detail: response.modal_data || {}
      }))
    } else {
      modal.innerHTML = ''
      modal.setAttribute('data-vue-component', 'vm-migrate-modal')
      modal.setAttribute('data-vue-props', JSON.stringify(response.modal_data || {}))
      modal.removeAttribute('data-vue-mounted')
      if (window.sccMountVueComponents) {
        window.sccMountVueComponents()
      }
    }
    sccShowModal(modal)
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to load migration dialog.')
  }
}

function handleConsoleAction(action, event = null, vmId = null) {
  const src = action?.dataAttrs?.['data-console-url'] || action?.href
  const title = action?.dataAttrs?.['data-console-title'] || 'VM Console'
  const statusBadgeText = action?.dataAttrs?.['data-console-status-badge'] || ''
  const statusBadgeTone = action?.dataAttrs?.['data-console-status-tone'] || 'normal'

  if (!src) {
    return
  }

  openSerialConsoleWindow(src, title, event, { statusBadgeText, statusBadgeTone, vmId })
  closeMenu()
}

function handleConsoleMenuAction(item, event = null, vmId = null) {
  if (!item?.href) {
    return
  }

  if (item.kind === 'serial' || item.kind === 'vnc') {
    openSerialConsoleWindow(item.href, item.title || 'VM Console', event, {
      statusBadgeText: item.statusBadgeText || '',
      statusBadgeTone: item.statusBadgeTone || 'normal',
      vmId
    })
  }

  closeConsoleMenu()
}

function openSerialConsoleWindow(src, title, event = null, options = {}) {
  const enterFrom = pointFromEvent(event)
  const statusBadgeText = options.statusBadgeText || ''
  const statusBadgeTone = options.statusBadgeTone || 'normal'
  const vmId = options.vmId != null ? String(options.vmId) : ''
  const existingWindow = serialConsoleWindows.value.find((item) => item.src === src)
  if (existingWindow) {
    serialConsoleWindows.value = serialConsoleWindows.value.map((item) =>
      item.id === existingWindow.id
        ? { ...item, title, statusBadgeText, statusBadgeTone, vmId: vmId || item.vmId || '' }
        : item
    )
    if (existingWindow.minimized) {
      restoreSerialConsoleWindow(existingWindow.id, enterFrom)
    } else {
      focusSerialConsoleWindow(existingWindow.id)
    }
    return
  }

  serialConsoleWindowSeed.value += 1
  serialConsoleZSeed.value += 1
  serialConsoleWindows.value = [
    ...serialConsoleWindows.value,
    {
      id: `serial-console-${serialConsoleWindowSeed.value}`,
      src,
      title,
      zIndex: serialConsoleZSeed.value,
      offset: (serialConsoleWindows.value.length % 6) * 20,
      minimized: false,
      enterFrom,
      statusBadgeText,
      statusBadgeTone,
      vmId
    }
  ]
}

function closeConsoleWindowsByVmId(vmId) {
  const targetVmId = String(vmId || '')
  if (!targetVmId) {
    return
  }

  serialConsoleWindows.value = serialConsoleWindows.value.filter((item) => String(item.vmId || '') !== targetVmId)
}

function minimizeSerialConsoleWindow(windowId) {
  serialConsoleWindows.value = serialConsoleWindows.value.map((item) =>
    item.id === windowId ? { ...item, minimized: true } : item
  )
}

function restoreSerialConsoleWindow(windowId, eventOrPoint = null) {
  const enterFrom = pointFromInput(eventOrPoint)
  serialConsoleZSeed.value += 1
  serialConsoleWindows.value = serialConsoleWindows.value.map((item) =>
    item.id === windowId ? { ...item, minimized: false, zIndex: serialConsoleZSeed.value, enterFrom } : item
  )
}

function closeSerialConsoleWindow(windowId) {
  serialConsoleWindows.value = serialConsoleWindows.value.filter((item) => item.id !== windowId)
}

function focusSerialConsoleWindow(windowId) {
  serialConsoleZSeed.value += 1
  serialConsoleWindows.value = serialConsoleWindows.value.map((item) =>
    item.id === windowId ? { ...item, zIndex: serialConsoleZSeed.value } : item
  )
}

function minimizeTargetPoint(windowId) {
  const dockIndex = minimizedConsoleWindows.value.length +
    visibleConsoleWindows.value.findIndex((item) => item.id === windowId)
  const safeIndex = Math.max(0, dockIndex)
  const bottomGap = 38
  const itemHeight = 56

  return {
    x: 170,
    y: window.innerHeight - bottomGap - (safeIndex * itemHeight)
  }
}

function pointFromInput(input) {
  if (!input) {
    return null
  }

  if (typeof input.x === 'number' && typeof input.y === 'number') {
    return { x: input.x, y: input.y }
  }

  return pointFromEvent(input)
}

function pointFromEvent(event) {
  const target = event?.currentTarget || event?.target
  if (!target || typeof target.getBoundingClientRect !== 'function') {
    return null
  }

  const rect = target.getBoundingClientRect()
  return {
    x: rect.left + (rect.width / 2),
    y: rect.top + (rect.height / 2)
  }
}

function replaceVmRow(vmId, rowHtml) {
  const currentRow = document.getElementById(`vm-row-${vmId}`)
  if (!currentRow || !rowHtml) {
    return
  }

  const template = document.createElement('template')
  template.innerHTML = rowHtml.trim()
  const nextRow = template.content.firstElementChild

  if (nextRow) {
    currentRow.replaceWith(nextRow)
  }
}

async function requestJson(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase()
  const headers = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(options.headers || {})
  }
  const fetchOptions = {
    method,
    headers,
    credentials: 'same-origin',
    cache: options.cache
  }

  if (csrfToken && method !== 'GET' && method !== 'HEAD') {
    headers['X-CSRF-Token'] = csrfToken
  }

  if (options.body) {
    fetchOptions.body = options.body
  }

  const response = await fetch(url, fetchOptions)
  const text = await response.text()
  let payload = null

  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    throw new Error(readResponseMessage(payload, text, options.fallbackError))
  }

  return payload
}

function readResponseMessage(payload, text, fallbackMessage) {
  if (payload && payload.message) {
    return payload.message
  }

  if (text) {
    return text
  }

  return fallbackMessage || 'Request failed.'
}

function normalizeFormAction(key, action, labelOverride = null) {
  if (!action) {
    return null
  }

  const enabled = Object.prototype.hasOwnProperty.call(action, 'enabled') ? !!action.enabled : !!action.form

  return {
    key,
    kind: 'form',
    visible: action.visible,
    enabled,
    form: action.form,
    progressMessage: action.progress_message,
    confirmMessage: action.confirm_message,
    icon: action.icon,
    label: labelOverride || action.label || titleToLabel(action.title, key),
    title: action.title,
    hiddenClass: action.hidden_class,
    vmName: action.vm_name
  }
}

function normalizeLinkAction(key, action, labelOverride = null) {
  if (!action) {
    return null
  }

  const dataAttrs = {}
  Object.entries(action.data || {}).forEach(([attrKey, value]) => {
    dataAttrs[`data-${attrKey.replaceAll('_', '-')}`] = value
  })

  return {
    key,
    kind: 'link',
    visible: action.visible,
    enabled: action.enabled,
    href: action.href,
    title: action.title,
    icon: action.icon,
    label: labelOverride || action.label || titleToLabel(action.title, key),
    hiddenClass: action.hidden_class,
    target: action.target,
    dataAttrs,
    openInModal: dataAttrs['data-open-in-modal'] === 'true',
    consoleOptions: dataAttrs['data-open-console-menu'] === 'true'
      ? [
          {
            key: 'vnc',
            kind: 'vnc',
            href: dataAttrs['data-console-vnc-url'],
            label: 'Open VNC',
            title: dataAttrs['data-console-vnc-title'] || 'VNC Console',
            icon: 'desktop',
            target: null,
            statusBadgeText: dataAttrs['data-console-vnc-status-badge'] || 'Connected (encrypted)',
            statusBadgeTone: dataAttrs['data-console-vnc-status-tone'] || 'normal'
          },
          {
            key: 'serial',
            kind: 'serial',
            href: dataAttrs['data-console-serial-url'],
            label: 'Open Serial',
            title: dataAttrs['data-console-title'] || 'Serial Console',
            icon: 'terminal',
            target: null,
            statusBadgeText: dataAttrs['data-console-serial-status-badge'] || 'Connected',
            statusBadgeTone: dataAttrs['data-console-serial-status-tone'] || 'normal'
          }
        ].filter((item) => !!item.href)
      : []
  }
}

function normalizeMigrationAction(migration) {
  if (!migration || !migration.visible) {
    return null
  }

  return {
    key: 'migration',
    kind: 'link',
    visible: migration.visible,
    enabled: migration.enabled,
    href: migration.url,
    title: 'Migration VM',
    label: 'Migrate',
    icon: 'migrate',
    hiddenClass: '',
    target: null,
    dataAttrs: migration.url ? { 'data-vm-migrate-url': migration.url } : {}
  }
}

function titleToLabel(title, key) {
  if (title) {
    return title
      .replace(/\s+VM$/i, '')
      .replace(/\s+information$/i, '')
      .replace(/\s+this$/i, '')
      .trim()
  }

  return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

function toggleMenu(rowId, event) {
  closeConsoleMenu()

  if (openMenuId.value === rowId) {
    openMenuId.value = null
    menuStyle.value = {}
    return
  }

  const trigger = event?.currentTarget
  const rect = trigger?.getBoundingClientRect()
  if (!rect) {
    openMenuId.value = rowId
    menuStyle.value = {}
    return
  }

  const menuWidth = 182
  const menuHeight = 144
  const gap = 8
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let left = rect.right - menuWidth
  let top = rect.bottom + gap

  if (left + menuWidth > viewportWidth - 12) {
    left = viewportWidth - menuWidth - 12
  }
  if (left < 12) {
    left = 12
  }

  if (top + menuHeight > viewportHeight - 12) {
    top = rect.top - menuHeight - gap
  }
  if (top < 12) {
    top = 12
  }

  openMenuId.value = rowId
  menuStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`
  }
}

function closeMenu() {
  openMenuId.value = null
  menuStyle.value = {}
}

function toggleConsoleMenu(rowId, event) {
  closeMenu()

  if (openConsoleMenuId.value === rowId) {
    closeConsoleMenu()
    return
  }

  const trigger = event?.currentTarget
  const rect = trigger?.getBoundingClientRect()
  if (!rect) {
    openConsoleMenuId.value = rowId
    consoleMenuStyle.value = {}
    return
  }

  const menuWidth = 182
  const menuHeight = 92
  const gap = 8
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let left = rect.right - menuWidth
  let top = rect.bottom + gap

  if (left + menuWidth > viewportWidth - 12) {
    left = viewportWidth - menuWidth - 12
  }
  if (left < 12) {
    left = 12
  }

  if (top + menuHeight > viewportHeight - 12) {
    top = rect.top - menuHeight - gap
  }
  if (top < 12) {
    top = 12
  }

  openConsoleMenuId.value = rowId
  consoleMenuStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    width: `${menuWidth}px`
  }
}

function closeConsoleMenu() {
  openConsoleMenuId.value = null
  consoleMenuStyle.value = {}
}

function handleDocumentClick(event) {
  if (!event.target.closest('.vm-list-more-actions__menu') && !event.target.closest('.vm-list-more-actions__gear')) {
    closeMenu()
  }

  if (!event.target.closest('.vm-list-console-actions__menu') && !event.target.closest('.vm-list-console-actions__trigger')) {
    closeConsoleMenu()
  }
}

function handleDocumentKeyup(event) {
  if (event.key !== 'Escape') {
    return
  }

  closeVmDetail()
}

async function handlePaginationClick(event) {
  const link = event.target.closest('.pagination a[data-page]')
  if (!link) {
    return
  }

  event.preventDefault()
  const page = link.dataset.page
  if (!page) {
    return
  }

  const url = new URL(window.location.href)
  url.searchParams.set('page', page)

  try {
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }

    const payload = await response.json()
    data.value = payload?.results_data || { rows: [], pagination: null, shell_id: 'vm-detail-shell', heading: 'Virtual Machines' }

    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, '', url.toString())
    }
  } catch (error) {
    console.error('Failed to update VM list pagination', error)
    window.alert('Failed to update the VM list.')
  }
}

function mountNestedVue() {
  if (window.sccMountVueComponents) {
    window.sccMountVueComponents()
  }
}

function handleOpenVmDetail(url) {
  if (!url) {
    return
  }

  requestJson(url, { method: 'GET' })
    .then((payload) => {
      const usePanel = shouldUseVmDetailPanel()
      const mode = usePanel ? 'panel' : 'modal'
      vmDetail.value = {
        open: true,
        mode,
        title: payload?.title || '',
        detailData: payload?.detail_data || null,
        ipRefreshUrl: payload?.ip_refresh_url || ''
      }

      const shell = document.getElementById(shellId.value)
      if (shell) {
        if (mode === 'panel') {
          shell.classList.add('vm-detail-shell-open')
          layoutVmDetailPanel()
        } else {
          shell.classList.remove('vm-detail-shell-open')
        }
      }
    })
    .catch((error) => {
      sccShowToast('danger', error?.message || 'Failed to load VM detail.')
    })
}

onMounted(() => {
  document.addEventListener('scc:vm-list-results:update', handleUpdate)
  document.addEventListener('scc:lazy-results:loading', handleLoading)
  document.addEventListener('scc:vm-list-row:update', handleRowUpdate)
  document.addEventListener('scc:vm-state-changed', handleVmStateChanged)
  document.addEventListener('click', handleDocumentClick)
  document.addEventListener('keyup', handleDocumentKeyup)
  window.addEventListener('resize', rerenderVmDetailForViewport)
  window.addEventListener('scroll', closeMenu, true)
  pollStorageMigrations()
  storageMigrationTimer = window.setInterval(pollStorageMigrations, 1000)
  nextTick(mountNestedVue)
})

onBeforeUnmount(() => {
  document.removeEventListener('scc:vm-list-results:update', handleUpdate)
  document.removeEventListener('scc:lazy-results:loading', handleLoading)
  document.removeEventListener('scc:vm-list-row:update', handleRowUpdate)
  document.removeEventListener('scc:vm-state-changed', handleVmStateChanged)
  document.removeEventListener('click', handleDocumentClick)
  document.removeEventListener('keyup', handleDocumentKeyup)
  window.removeEventListener('resize', rerenderVmDetailForViewport)
  window.removeEventListener('scroll', closeMenu, true)
  if (storageMigrationTimer) {
    window.clearInterval(storageMigrationTimer)
    storageMigrationTimer = null
  }
})

watch(data, () => {
  nextTick(mountNestedVue)
})
</script>

<style scoped>
.vm-list-vue-results {
  position: relative;
  min-height: 120px;
}

.vm-list-vue-results-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 253, 0.82);
  backdrop-filter: blur(1px);
}

.vm-list-vue-results-loader {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid rgba(130, 150, 175, 0.24);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  color: #36506a;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 16px 34px rgba(122, 141, 164, 0.16);
}

.vm-list-vue-results-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(116, 143, 171, 0.22);
  border-top-color: #4d6d8f;
  border-radius: 999px;
  animation: vm-list-spin 0.9s linear infinite;
}

.vm-list-action-buttons {
  justify-content: center;
  gap: 4px;
  flex-wrap: nowrap;
}

.vm-list-table {
  table-layout: fixed;
}

.vm-list-col--status {
  width: 92px;
}

.vm-list-col--cpu {
  width: 60px;
}

.vm-list-col--memory {
  width: 92px;
}

.vm-list-col--boot {
  width: 84px;
}

.vm-list-col--network {
  width: 110px;
}

.vm-list-col--owner {
  width: 110px;
}

.vm-list-col--actions {
  width: 74px;
}

.vm-list-name-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.vm-list-name-comment {
  margin-top: 4px;
  color: #6b7c8f;
  font-size: 12px;
  line-height: 1.35;
}

.vm-list-storage-migration {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 6px;
  color: #2563a6;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  text-decoration: none;
  white-space: nowrap;
}

.vm-list-storage-migration:hover,
.vm-list-storage-migration:focus {
  color: #174f87;
  text-decoration: underline;
}

.vm-list-storage-migration__spinner {
  width: 10px;
  height: 10px;
  border: 2px solid #bfd5ea;
  border-top-color: #2563a6;
  border-radius: 50%;
  animation: vm-list-spin 0.8s linear infinite;
}

.vm-list-storage-migration__percent {
  color: #5d7287;
  font-variant-numeric: tabular-nums;
}

.vm-list-more-actions {
  position: relative;
}

.vm-list-more-actions__gear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 44px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d4dde8;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #eef3f8 100%);
  color: #4a5f75;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.vm-list-more-actions__gear:hover,
.vm-list-more-actions__gear:focus {
  transform: translateY(-1px);
  border-color: #b8c9da;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
}

.vm-list-more-actions__caret {
  font-size: 11px;
  opacity: 0.75;
}

.vm-list-more-actions__menu {
  z-index: 60;
  min-width: 182px;
  padding: 8px;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 20px 38px rgba(15, 23, 42, 0.16);
}

.vm-list-console-actions__trigger {
  gap: 4px;
}

.vm-list-console-actions__caret {
  opacity: 0.72;
}

.vm-list-more-actions__form {
  display: block;
}

.vm-list-more-actions__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #425466;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
}

.vm-list-more-actions__item:hover {
  background: #eff4f9;
  color: #27445f;
  text-decoration: none;
}

.vm-list-more-actions__item--danger,
button.vm-list-more-actions__item--danger {
  color: #9f1d2d;
}

.vm-list-more-actions__item--danger:hover,
button.vm-list-more-actions__item--danger:hover {
  background: #fff1f2;
  color: #7f1722;
}

.vm-list-more-actions__item--disabled {
  color: #93a1b1;
  cursor: not-allowed;
}

.vm-list-more-actions__item--disabled:hover {
  background: transparent;
  color: #93a1b1;
}

.vm-console-dock {
  position: fixed;
  left: 14px;
  bottom: 14px;
  z-index: 1200;
}

.vm-console-dock__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.vm-console-dock__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border: 1px solid rgba(154, 170, 190, 0.5);
  border-radius: 12px;
  background: rgba(246, 249, 253, 0.97);
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.16);
}

.vm-console-dock__restore {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 280px;
  padding: 6px 10px;
  border: 0;
  border-radius: 9px;
  background: #eef3f9;
  color: #2d4156;
  font-size: 12px;
  font-weight: 600;
}

.vm-console-dock__restore span {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vm-console-dock__restore:hover {
  background: #dde8f3;
}

.vm-console-dock__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 9px;
  background: #f4d9de;
  color: #8f2030;
}

.vm-console-dock__close:hover {
  background: #efc7cf;
}

.console-dock-transition-enter-active,
.console-dock-transition-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.console-dock-transition-enter-from,
.console-dock-transition-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

.console-dock-transition-move {
  transition: transform 0.2s ease;
}

@keyframes vm-list-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
